/**
 * Draggable preset row wrapper
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { cn } from '@/lib/utils';

import type { Preset } from '../shared';
import { PresetRow } from './PresetRow';

export interface SortablePresetRowProps {
  preset: Preset;
  tagColors?: Record<string, string>;
  selectedDrafts: PreparedDrop[];
  canAddToPreset: boolean;
  onApply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddToPreset?: () => void;
  isApplying: boolean;
  isBusy: boolean;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}

export const SortablePresetRow: FC<SortablePresetRowProps> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.preset.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex items-stretch gap-1', isDragging && 'invisible')}
    >
      <button
        type="button"
        className={cn(
          'flex shrink-0 cursor-grab touch-none items-center justify-center px-2 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          isDragging && 'cursor-grabbing',
        )}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <PresetRow
          preset={props.preset}
          tagColors={props.tagColors}
          selectedDrafts={props.selectedDrafts}
          canAddToPreset={props.canAddToPreset}
          onApply={props.onApply}
          onEdit={props.onEdit}
          onDelete={props.onDelete}
          onAddToPreset={props.onAddToPreset}
          isApplying={props.isApplying}
          isBusy={props.isBusy}
          selectionMode={props.selectionMode}
          selected={props.selected}
          onToggleSelected={props.onToggleSelected}
        />
      </div>
    </div>
  );
};
